import type { Layout } from '../../../data/types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { intersection } from '../../../geo/clip';
import { polygonArea, ringBounds } from '../../../geo/polygon';
import { createLotPlacer } from '../lotPlacer';
import { footprintInside, lotsOverlap, obliqueCorners } from '../lotFootprint';
import { measureLine } from '../measuredLine';
import type { Lot, ReservedFootprint } from '../types';
import { LOT } from '../lotRules';
import type { SurveyedRow } from './rowTypes';
import { readFronts } from './rowFronts';

export interface ObliqueRow extends SurveyedRow {
  /** Estimated lateral back-wall shift per metre of normal depth, guided by retained lanes. */
  readonly shear: number;
  /** Optional shared estimated plot direction, preserving parallel side walls at street bends. */
  readonly plotAxis?: Vec2;
}

function plotShear(row: ObliqueRow, facing: Vec2): number {
  if (!row.plotAxis) return row.shear;
  const inward: Vec2 = [-facing[0], -facing[1]],
    right: Vec2 = [-facing[1], facing[0]],
    normal = row.plotAxis[0] * inward[0] + row.plotAxis[1] * inward[1];
  return Math.abs(normal) < 1e-8
    ? row.shear
    : (row.plotAxis[0] * right[0] + row.plotAxis[1] * right[1]) / normal;
}

/** Share a narrowing block between opposing rows, rather than letting the first row take it. */
function pairedDepth(
  fronts: readonly Vec2[],
  facing: Vec2,
  row: ObliqueRow,
  layout: Layout
): number {
  const other = layout.streets.find((s) => s.id === row.opposite);
  if (!other) return row.depth;
  const right: Vec2 = [-facing[1], facing[0]];
  const dir: Vec2 = [-facing[0] + right[0] * row.shear, -facing[1] + right[1] * row.shear];
  let limit = row.depth;
  for (const a of fronts) {
    for (let i = 1; i < other.centerline.length; i++) {
      const p = other.centerline[i - 1]!,
        q = other.centerline[i]!;
      const edge: Vec2 = [q[0] - p[0], q[1] - p[1]];
      const delta: Vec2 = [p[0] - a[0], p[1] - a[1]];
      const cross = dir[0] * edge[1] - dir[1] * edge[0];
      if (Math.abs(cross) < 1e-8) continue;
      const t = (delta[0] * edge[1] - delta[1] * edge[0]) / cross;
      const u = (delta[0] * dir[1] - delta[1] * dir[0]) / cross;
      if (t <= 0 || u < 0 || u > 1) continue;
      const normalSpeed = Math.abs(cross) / Math.hypot(...edge);
      const farFront = t - (other.width / 2 + LOT.sourceOffset) / normalSpeed;
      // Two metres of estimated rear space; this is a fitting rule, not a traced yard edge.
      limit = Math.min(limit, (farFront - 2) / 2);
    }
  }
  return limit;
}

export function obliqueRowLots(
  rows: readonly ObliqueRow[],
  layout: Layout,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[],
  ordinalBase: number
): readonly Lot[] {
  const placed: Lot[] = [];
  const reservations = reserved.map((r) => ({
    polygon: r.polygon,
    box: ringBounds(r.polygon.outer),
  }));
  const fits = (lot: Lot): boolean => {
    if (!footprintInside(buildable, lot.corners)) return false;
    const box = ringBounds(lot.corners);
    if (
      reservations.some(
        ({ polygon, box: b }) =>
          b.maxX >= box.minX &&
          b.minX <= box.maxX &&
          b.maxY >= box.minY &&
          b.minY <= box.maxY &&
          intersection([{ outer: [...lot.corners], holes: [] }], [polygon]).some(
            (p) => polygonArea(p) > 0.001
          )
      )
    )
      return false;
    return !placed.some((p) => lotsOverlap(lot, p, 0.001));
  };
  const placer = createLotPlacer((p) => buildable.contains(p), [], reserved);
  rows.forEach((row, rowIndex) => {
    const source = layout.streets.find((s) => s.id === row.source);
    if (!source) return;
    const line = measureLine(
      source.centerline,
      source.centerline.map(() => source.width)
    );
    if (Math.max(row.from, row.to) > line.length) return;
    const make = (front: Vec2, facing: Vec2, width: number, depth: number): Lot => {
      const right: Vec2 = [-facing[1], facing[0]],
        shear = plotShear(row, facing);
      return {
        index: placed.length,
        sourceId: source.id,
        side: row.side,
        ordinal: ordinalBase + rowIndex * 100,
        kind: 'house',
        facing,
        frontage: width,
        depth,
        plot: { frontMid: front, shear },
        corners: obliqueCorners(front, facing, width, depth, shear),
        obb: {
          center: [
            front[0] - (facing[0] * depth) / 2 + (right[0] * shear * depth) / 2,
            front[1] - (facing[1] * depth) / 2 + (right[1] * shear * depth) / 2,
          ],
          axisU: right,
          halfU: width / 2 + (Math.abs(shear) * depth) / 2,
          halfV: depth / 2,
        },
      };
    };
    const masses = readFronts(row, line, source.width, buildable, placer, (a, b, depth, facing) =>
      fits(
        make(
          [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2],
          facing,
          Math.hypot(b[0] - a[0], b[1] - a[1]),
          depth
        )
      )
    );
    const plotIds = row.houses.flatMap((h) => h.plots?.map((p) => p.id) ?? [h.id]);
    const fronts = masses.flatMap(({ from, to, house }) => {
      const massMid = (from + to) / 2;
      if (!house.plots)
        return [
          {
            from,
            to,
            massMid,
            house,
            id: house.id,
            evidence: row.evidence,
            ordinal: row.houses.indexOf(house),
          },
        ];
      const weight = house.plots.reduce((n, p) => n + p.widthWeight, 0);
      let along = from;
      return house.plots.map((p) => {
        const end = along + ((to - from) * p.widthWeight) / weight;
        const front = {
          from: along,
          to: end,
          massMid,
          house,
          id: p.id,
          evidence: 'inferred' as const,
          ordinal: p.ordinal ?? plotIds.indexOf(p.id),
        };
        along = end;
        return front;
      });
    });
    for (const { from, to, massMid, house, id, evidence, ordinal } of fronts) {
      const massFrame = line.pointAt(massMid),
        tangent = massFrame.tangent;
      const shift = (from + to) / 2 - massMid;
      // Subdivisions share their parent axis, including where the source street bends inside
      // a mass. Re-reading each child's tangent can rotate it out of a fitting parent plot.
      const point: Vec2 = [
        massFrame.point[0] + tangent[0] * shift,
        massFrame.point[1] + tangent[1] * shift,
      ];
      const inward: Vec2 =
        row.side === 'left' ? [-tangent[1], tangent[0]] : [tangent[1], -tangent[0]];
      const facing: Vec2 = [-inward[0], -inward[1]];
      const front: Vec2 = [
        point[0] + inward[0] * (source.width / 2 + (row.frontOffset ?? LOT.sourceOffset)),
        point[1] + inward[1] * (source.width / 2 + (row.frontOffset ?? LOT.sourceOffset)),
      ];
      const span = Math.abs(to - from);
      let width = span - 0.08;
      while (width > Math.max(3.5, span * 0.8) && !fits(make(front, facing, width, 6)))
        width -= 0.2;
      const frontCorners = make(front, facing, width, 0).corners.slice(0, 2);
      let depth =
        Math.floor(
          pairedDepth(frontCorners, facing, { ...row, shear: plotShear(row, facing) }, layout) * 4
        ) / 4;
      while (depth >= 6 && !fits(make(front, facing, width, depth))) depth -= 0.25;
      if (depth < 6) continue;
      placed.push({
        ...make(front, facing, width, depth),
        ordinal: ordinalBase + rowIndex * 100 + ordinal,
        reconstruction: {
          id,
          row: row.id,
          evidence,
          ...(house.paintedPixel ? { paintedPixel: house.paintedPixel } : {}),
          params: {
            storeys: house.storeys,
            storeyHeight: 3.05,
            roofMaterial: house.roof,
            ...(house.gable ? { frontGable: house.gable } : {}),
          },
        },
      });
    }
  });
  return placed;
}
